import React, {
  forwardRef,
  Ref,
  RefAttributes,
  type JSX,
  useCallback,
  HTMLAttributes,
  ReactElement,
  useMemo,
} from "react";

import { SelectChangeEvent } from "@mui/material/Select";
import classnames from "classnames";
import { FieldValues, useController } from "react-hook-form";

import { getLabel, isInputRequired } from "../../../helpers/validators";
import { useCurrentInputVibe } from "../../../hooks/useCurrentInputVibe";
import { useCustomError } from "../../../hooks/useCustomError";
import { useCustomInputProps } from "../../../hooks/useCustomInputProps";
import { DashboardBaseInput } from "../BaseInput";
import { DashboardInputLabel } from "../InputLabel/DashboardInputLabel";
import { useMergeClasses } from "../useMergeClasses";

import { zeffySelectClasses, StyledSelect } from "./styles";
import { SelectProps, BaseSelectProps, FormSelectProps } from "./types";

import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { useValidators } from "@/hooks/useValidators";
import { ChevronDown } from "@/icons/outlined";
function SelectInner<T extends FieldValues>(
  { control, rules, name, ...rest }: SelectProps<T>,
  ref: Ref<HTMLSelectElement>
) {
  return control && name ? (
    <FormSelect {...rest} ref={ref} control={control} name={name} rules={rules} />
  ) : (
    <BaseSelect {...rest} name={name} ref={ref} />
  );
}

SelectInner.displayName = "Select";

export const DashboardSelect = forwardRef(SelectInner) as <T extends FieldValues>(
  props: SelectProps<T> & RefAttributes<HTMLSelectElement>
) => JSX.Element;

function FormSelectInner<T extends FieldValues>(
  { control, defaultValue, label, name, onBlur, onChange, rules, noAsterisk, ...rest }: FormSelectProps<T>,
  ref: Ref<HTMLSelectElement>
) {
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);

  const { field } = useController<T>({
    name,
    control,
    defaultValue: defaultValue as never,
    rules: validators,
  });

  const handleChange: BaseSelectProps["onChange"] = useCallback(
    (event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
      onChange?.(event, child);
      field.onChange(event.target.value);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    field.onBlur();
  }, [field, onBlur]);

  return (
    <BaseSelect
      {...rest}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      value={field.value}
      label={getLabel(isRequired, label, noAsterisk)}
      name={name}
    />
  );
}

FormSelectInner.displayName = "FormSelect";

const FormSelect = forwardRef(FormSelectInner) as unknown as <T extends FieldValues>(
  props: FormSelectProps<T> & RefAttributes<HTMLSelectElement>
) => JSX.Element;

export const disabledAutoFocusMenuProps = {
  disableAutoFocus: true,
  autoFocus: false,
};

const BaseSelect = forwardRef<HTMLSelectElement, BaseSelectProps>(
  (
    {
      children,
      className,
      classes: externalClasses,
      vibe,
      "data-test": dataTest,
      disableAutoFocus,
      disabled,
      disableAutocomplete,
      error,
      errorMessage,
      headerRight,
      helperText,
      inputProps,
      inputLabelClasses,
      label,
      MenuProps,
      size,
      tooltipText,
      name,
      ...rest
    },
    ref
  ): ReactElement<BaseSelectProps> => {
    const currenVibe = useCurrentInputVibe(vibe);
    const classes = useMergeClasses(zeffySelectClasses, externalClasses);

    const currentError = useCustomError(error, errorMessage);
    const customInputProps = useCustomInputProps({ inputProps, disableAutocomplete });

    const mergedMenuProps = useMemo(
      () => ({
        ...MenuProps,
        ...(disableAutoFocus ? disabledAutoFocusMenuProps : undefined),
      }),
      [MenuProps, disableAutoFocus]
    );

    const renderBaseSelectIcon = useCallback(
      (props: { className?: string; open?: boolean }) => {
        return (
          <DashboardIcon
            inline
            size="extraSmall"
            vibe="inherit"
            className={classnames(
              props.className,
              classes.icon,
              { [classes.iconForm]: currenVibe === "form" },
              { [classes.iconOpen]: props.open }
            )}
            data-test={`${dataTest}-button`}
          >
            <ChevronDown />
          </DashboardIcon>
        );
      },
      [classes.icon, classes.iconForm, classes.iconOpen, currenVibe, dataTest]
    );

    const SelectDisplayProps = useMemo(
      () =>
        ({
          ["data-test"]: dataTest,
        }) as HTMLAttributes<HTMLDivElement>,
      [dataTest]
    );

    return (
      <DashboardInputLabel
        className={className}
        classes={inputLabelClasses}
        vibe={currenVibe}
        errorMessage={errorMessage}
        headerRight={headerRight}
        helperText={helperText}
        label={label}
        tooltipText={tooltipText}
        data-test={`${dataTest}-label`}
      >
        <StyledSelect
          variant="standard"
          {...rest}
          value={rest.value ?? ""}
          displayEmpty
          MenuProps={mergedMenuProps}
          ref={ref}
          classes={{
            root: classes.root,
            select: classnames(classes.select, {
              [classes.medium]: size !== "small",
              [classes.small]: size === "small",
            }),
          }}
          disabled={disabled}
          IconComponent={renderBaseSelectIcon}
          inputProps={customInputProps}
          SelectDisplayProps={SelectDisplayProps}
          input={
            <DashboardBaseInput vibe={currenVibe} disabled={disabled} error={currentError} size={size} noPadding />
          }
          name={name}
        >
          {children}
        </StyledSelect>
      </DashboardInputLabel>
    );
  }
);

BaseSelect.displayName = "Select";
